import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Autocomplete,
  Chip,
  Grid,
  Icon,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Alert,
  OutlinedInput,
  ListItemText,
  Checkbox,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { toast } from "react-toastify";
import { rfqService } from "../../services/rfq.service";
import { supplierService } from "../../services/supplier.service";
import {
  RFQ,
  PurchaseRequest,
  CreateRFQRequest,
  UpdateRFQRequest,
  SupplierInfo,
  GetAvailablePRsRequest,
} from "../../types/rfq.types";
import { IESDoc } from "../../../../app.interface";

interface RFQFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  rfq?: RFQ | null;
  loading?: boolean;
}

interface RFQFormData {
  purchase_request_id: string;
  purchase_request_number: string;
  selected_suppliers: string[];
  due_date: Date | null;
  notes: string;
  branch_id?: string;
}

interface Supplier {
  _id: string;
  name: string;
  address?: string;
  contacts?: Array<{
    name?: string;
    email?: string;
    phone?: string;
  }>;
  website_url?: string;
}

export const RFQFormDialog: React.FC<RFQFormDialogProps> = ({
  open,
  onClose,
  onSave,
  rfq,
  loading = false,
}) => {
  const [formData, setFormData] = useState<RFQFormData>({
    purchase_request_id: "",
    purchase_request_number: "",
    selected_suppliers: [],
    due_date: null,
    notes: "",
    branch_id: "",
  });

  const [purchaseRequests, setPurchaseRequests] = useState<PurchaseRequest[]>(
    []
  );
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [selectedPR, setSelectedPR] = useState<PurchaseRequest | null>(null);
  const [prLoading, setPrLoading] = useState(false);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    if (open) {
      if (rfq) {
        // Edit mode - populate form with existing RFQ data
        setFormData({
          purchase_request_id: rfq.purchase_request_id,
          purchase_request_number: rfq.purchase_request_number,
          selected_suppliers: rfq.selected_suppliers,
          due_date: rfq.due_date ? new Date(rfq.due_date) : null,
          notes: rfq.notes || "",
          branch_id: rfq.branch_id || "",
        });
      } else {
        // Create mode - reset form
        setFormData({
          purchase_request_id: "",
          purchase_request_number: "",
          selected_suppliers: [],
          due_date: null,
          notes: "",
          branch_id: "",
        });
        loadAvailablePurchaseRequests();
      }
      loadSuppliers();
    }
  }, [open, rfq]);

  const loadAvailablePurchaseRequests = async () => {
    setPrLoading(true);
    try {
      const params: GetAvailablePRsRequest = {
        pageIndex: 0,
        pageSize: 100,
      };

      const response = await rfqService.getAvailablePurchaseRequests(params);
      console.log("Available PRs response:", response); // Debug log

      if (response.success && response.data) {
        const prs = response.data.purchaseRequests || [];
        console.log("Setting purchase requests:", prs); // Debug log
        setPurchaseRequests(prs);

        if (prs.length === 0) {
          toast.info(
            "No approved Purchase Requests available for RFQ creation"
          );
        }
      } else {
        console.error("Response not successful:", response);
        throw new Error(
          response.error?.message || "Failed to load purchase requests"
        );
      }
    } catch (error: any) {
      console.error("Error loading purchase requests:", error);
      toast.error(error.message || "Failed to load purchase requests");
      setPurchaseRequests([]);
    } finally {
      setPrLoading(false);
    }
  };

  const loadSuppliers = async () => {
    try {
      setSuppliersLoading(true);
      console.log("Loading suppliers...");

      const response = await supplierService.listSuppliers({
        pageSize: 25, // Get all suppliers
        sortField: "name.keyword",
        sortOrder: "asc",
      });

      console.log("Suppliers response:", response);

      if (response?.data) {
        // Transform the data to match our Supplier interface
        const transformedSuppliers: Supplier[] = response.data
          .filter((supplier: IESDoc) => !supplier.is_deleted) // Only active suppliers
          .map((supplier: IESDoc) => ({
            _id: supplier._id,
            name: supplier.name || "Unknown Supplier",
            address: supplier.address,
            contacts: supplier.contacts || [],
            website_url: supplier.website_url,
          }));

        console.log("Transformed suppliers:", transformedSuppliers);
        setSuppliers(transformedSuppliers);
      } else {
        console.warn("No supplier data received");
        setSuppliers([]);
      }
    } catch (error) {
      console.error("Error loading suppliers:", error);
      toast.error("Failed to load suppliers");
      setSuppliers([]);
    } finally {
      setSuppliersLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.purchase_request_id) {
      errors.purchase_request_id = "Please select a purchase request";
    }

    if (formData.selected_suppliers.length === 0) {
      errors.selected_suppliers = "Please select at least one supplier";
    }

    if (!formData.due_date) {
      errors.due_date = "Please select a due date";
    } else if (formData.due_date <= new Date()) {
      errors.due_date = "Due date must be in the future";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      if (rfq) {
        // Update existing RFQ
        const updateData: UpdateRFQRequest = {
          _id: rfq._id,
          selected_suppliers: formData.selected_suppliers,
          due_date: formData.due_date?.getTime() || Date.now(),
          notes: formData.notes,
          // Only include branch_id if it has a value
          ...(formData.branch_id && { branch_id: formData.branch_id }),
        };

        const response = await rfqService.updateRFQ(updateData);
        if (response.success) {
          toast.success("RFQ updated successfully");
          onSave();
          onClose();
        } else {
          throw new Error(response.error?.message || "Failed to update RFQ");
        }
      } else {
        // Create new RFQ
        const createData: CreateRFQRequest = {
          purchase_request_id: formData.purchase_request_id,
          selected_suppliers: formData.selected_suppliers,
          due_date: formData.due_date?.getTime() || Date.now(),
          notes: formData.notes,
          // Only include branch_id if it has a value
          ...(formData.branch_id && { branch_id: formData.branch_id }),
        };

        const response = await rfqService.createRFQ(createData);
        if (response.success) {
          toast.success("RFQ created successfully");
          onSave();
          onClose();
        } else {
          throw new Error(response.error?.message || "Failed to create RFQ");
        }
      }
    } catch (error: any) {
      console.error("Error saving RFQ:", error);
      toast.error(error.message || "Failed to save RFQ");
    } finally {
      setSaving(false);
    }
  };

  const handlePurchaseRequestChange = (prId: string) => {
    const pr = purchaseRequests.find((p) => p._id === prId);
    if (pr) {
      setSelectedPR(pr);
      setFormData((prev) => ({
        ...prev,
        purchase_request_id: prId,
        purchase_request_number: pr.requestId,
      }));
    }
  };

  const formatDate = (timestamp: number | string) => {
    // Handle both timestamp and ISO string formats
    const date =
      typeof timestamp === "string" ? new Date(timestamp) : new Date(timestamp);
    return date.toLocaleDateString();
  };

  const renderSupplierOption = (supplier: Supplier) => {
    const primaryContact = supplier.contacts?.[0];
    const contactInfo = primaryContact
      ? `${primaryContact.email || ""} • ${primaryContact.phone || ""}`.replace(
          /^• |• $/,
          ""
        )
      : "";

    return (
      <Box key={supplier._id} sx={{ py: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {supplier.name}
        </Typography>
        {contactInfo && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block" }}
          >
            {contactInfo}
          </Typography>
        )}
        {supplier.address && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block" }}
          >
            {supplier.address}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: "60vh" },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                bgcolor: "primary.main",
                borderRadius: "8px",
                p: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon sx={{ color: "white", fontSize: 20 }}>request_quote</Icon>
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {rfq ? "Edit RFQ" : "Create New RFQ"}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {rfq
                  ? `RFQ: ${rfq.rfq_number}`
                  : "Request quotations from suppliers"}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              {/* Purchase Request Selection */}
              {!rfq && (
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    error={!!validationErrors.purchase_request_id}
                  >
                    <InputLabel>Purchase Request *</InputLabel>
                    <Select
                      value={formData.purchase_request_id}
                      onChange={(e) =>
                        handlePurchaseRequestChange(e.target.value)
                      }
                      label="Purchase Request *"
                      disabled={prLoading}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 400,
                            "& .MuiMenuItem-root": {
                              whiteSpace: "normal",
                            },
                          },
                        },
                      }}
                    >
                      {prLoading ? (
                        <MenuItem disabled>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Loading purchase requests...
                        </MenuItem>
                      ) : purchaseRequests.length === 0 ? (
                        <MenuItem disabled>
                          No approved purchase requests available
                        </MenuItem>
                      ) : (
                        purchaseRequests.map((pr) => (
                          <MenuItem
                            key={pr._id}
                            value={pr._id}
                            sx={{ py: 1.5 }}
                          >
                            <Box sx={{ width: "100%" }}>
                              {/* PR Header */}
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  mb: 1,
                                }}
                              >
                                <Typography variant="body1" fontWeight={600}>
                                  {pr.requestId}
                                </Typography>
                                <Chip
                                  label={`${pr.items?.length || 0} items`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: 11 }}
                                />
                              </Box>

                              {/* PR Items Preview */}
                              {pr.items && pr.items.length > 0 && (
                                <Box sx={{ mb: 1 }}>
                                  {pr.items.slice(0, 2).map((item, index) => (
                                    <Typography
                                      key={index}
                                      variant="caption"
                                      sx={{
                                        display: "block",
                                        color: "text.secondary",
                                        lineHeight: 1.3,
                                      }}
                                    >
                                      • {item.item_name} ({item.quantity}{" "}
                                      {item.uom})
                                      {item.urgency && (
                                        <Chip
                                          label={item.urgency}
                                          size="small"
                                          sx={{
                                            ml: 1,
                                            height: 16,
                                            fontSize: 10,
                                            bgcolor:
                                              item.urgency === "high"
                                                ? "#ffebee"
                                                : item.urgency === "medium"
                                                ? "#fff3e0"
                                                : "#e8f5e8",
                                            color:
                                              item.urgency === "high"
                                                ? "#c62828"
                                                : item.urgency === "medium"
                                                ? "#ef6c00"
                                                : "#2e7d32",
                                          }}
                                        />
                                      )}
                                    </Typography>
                                  ))}
                                  {pr.items.length > 2 && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "primary.main",
                                        fontStyle: "italic",
                                      }}
                                    >
                                      +{pr.items.length - 2} more items...
                                    </Typography>
                                  )}
                                </Box>
                              )}

                              {/* PR Metadata */}
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                Created {formatDate(pr.created_at)} •{" "}
                                {pr.created_by_email ||
                                  pr.created_by ||
                                  "Unknown"}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {validationErrors.purchase_request_id && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 1 }}
                      >
                        {validationErrors.purchase_request_id}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
              )}

              {/* Selected Purchase Request Details (Create Mode) */}
              {!rfq && selectedPR && (
                <Grid item xs={12}>
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: "grey.50",
                      border: "1px solid",
                      borderColor: "primary.main",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 2,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          Selected Purchase Request
                        </Typography>
                        <Typography
                          variant="h6"
                          fontWeight={600}
                          sx={{ mb: 1 }}
                        >
                          {selectedPR.requestId}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Created {formatDate(selectedPR.created_at)} by{" "}
                          {selectedPR.created_by_email ||
                            selectedPR.created_by ||
                            "Unknown"}
                        </Typography>
                      </Box>
                      <Chip
                        label={`${selectedPR.items?.length || 0} items`}
                        color="primary"
                        variant="outlined"
                      />
                    </Box>

                    {/* Items List */}
                    {selectedPR.items && selectedPR.items.length > 0 && (
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 1, color: "text.primary" }}
                        >
                          Items to Quote:
                        </Typography>
                        <Box sx={{ display: "grid", gap: 1 }}>
                          {selectedPR.items.map((item, index) => (
                            <Box
                              key={index}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                p: 1,
                                bgcolor: "background.paper",
                                borderRadius: 1,
                                border: "1px solid",
                                borderColor: "divider",
                              }}
                            >
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" fontWeight={500}>
                                  {item.item_name}
                                </Typography>
                                {item.reason && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Reason: {item.reason}
                                  </Typography>
                                )}
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography variant="body2" fontWeight={600}>
                                  {item.quantity} {item.uom}
                                </Typography>
                                {item.urgency && (
                                  <Chip
                                    label={item.urgency}
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: 11,
                                      bgcolor:
                                        item.urgency === "high"
                                          ? "#ffebee"
                                          : item.urgency === "medium"
                                          ? "#fff3e0"
                                          : "#e8f5e8",
                                      color:
                                        item.urgency === "high"
                                          ? "#c62828"
                                          : item.urgency === "medium"
                                          ? "#ef6c00"
                                          : "#2e7d32",
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              )}

              {/* Purchase Request Display (Edit Mode) */}
              {rfq && (
                <Grid item xs={12}>
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: "grey.50",
                      border: "1px solid",
                      borderColor: "primary.main",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 2,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          Purchase Request
                        </Typography>
                        <Typography
                          variant="h6"
                          fontWeight={600}
                          sx={{ mb: 1 }}
                        >
                          {rfq.purchase_request_number}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Created {formatDate(rfq.created_at)}
                        </Typography>
                      </Box>
                      {rfq.pr_items && (
                        <Chip
                          label={`${rfq.pr_items.length} items`}
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    {/* Items List for Edit Mode */}
                    {rfq.pr_items && rfq.pr_items.length > 0 && (
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 1, color: "text.primary" }}
                        >
                          Items to Quote:
                        </Typography>
                        <Box sx={{ display: "grid", gap: 1 }}>
                          {rfq.pr_items.map((item, index) => (
                            <Box
                              key={index}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                p: 1,
                                bgcolor: "background.paper",
                                borderRadius: 1,
                                border: "1px solid",
                                borderColor: "divider",
                              }}
                            >
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" fontWeight={500}>
                                  {item.item_name}
                                </Typography>
                                {item.reason && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Reason: {item.reason}
                                  </Typography>
                                )}
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography variant="body2" fontWeight={600}>
                                  {item.quantity} {item.uom}
                                </Typography>
                                {item.urgency && (
                                  <Chip
                                    label={item.urgency}
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: 11,
                                      bgcolor:
                                        item.urgency === "high"
                                          ? "#ffebee"
                                          : item.urgency === "medium"
                                          ? "#fff3e0"
                                          : "#e8f5e8",
                                      color:
                                        item.urgency === "high"
                                          ? "#c62828"
                                          : item.urgency === "medium"
                                          ? "#ef6c00"
                                          : "#2e7d32",
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              )}

              {/* Supplier Selection */}
              <Grid item xs={12}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Suppliers *</InputLabel>
                  <Select
                    multiple
                    value={formData.selected_suppliers}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        selected_suppliers: e.target.value as string[],
                      }))
                    }
                    input={<OutlinedInput label="Suppliers *" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => {
                          const supplier = suppliers.find(
                            (s) => s._id === value
                          );
                          return (
                            <Chip
                              key={value}
                              label={supplier?.name || value}
                              size="small"
                            />
                          );
                        })}
                      </Box>
                    )}
                    disabled={suppliersLoading}
                  >
                    {suppliersLoading ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Loading suppliers...
                      </MenuItem>
                    ) : suppliers.length === 0 ? (
                      <MenuItem disabled>No suppliers available</MenuItem>
                    ) : (
                      suppliers.map((supplier) => (
                        <MenuItem key={supplier._id} value={supplier._id}>
                          <Checkbox
                            checked={
                              formData.selected_suppliers.indexOf(
                                supplier._id
                              ) > -1
                            }
                          />
                          <ListItemText
                            primary={
                              <Box>
                                <Typography variant="subtitle2">
                                  {supplier.name}
                                </Typography>
                                {supplier.contacts?.[0]?.email && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {supplier.contacts[0].email}
                                    {supplier.contacts[0].phone &&
                                      ` • ${supplier.contacts[0].phone}`}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  <FormHelperText>
                    Select suppliers to request quotations from
                    {formData.selected_suppliers.length > 0 &&
                      ` (${formData.selected_suppliers.length} selected)`}
                  </FormHelperText>
                </FormControl>
              </Grid>

              {/* Selected Suppliers Preview */}
              {formData.selected_suppliers.length > 0 && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      mb: 2,
                      p: 2,
                      bgcolor: "background.paper",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, color: "primary.main" }}
                    >
                      Selected Suppliers ({formData.selected_suppliers.length})
                    </Typography>
                    {formData.selected_suppliers.map((supplierId) => {
                      const supplier = suppliers.find(
                        (s) => s._id === supplierId
                      );
                      if (!supplier) return null;
                      return renderSupplierOption(supplier);
                    })}
                  </Box>
                </Grid>
              )}

              {/* Due Date */}
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Due Date *"
                  value={formData.due_date}
                  onChange={(date) =>
                    setFormData((prev) => ({ ...prev, due_date: date }))
                  }
                  minDate={new Date()}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      error={!!validationErrors.due_date}
                      helperText={validationErrors.due_date}
                    />
                  )}
                />
              </Grid>

              {/* Branch (Optional) */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Branch"
                  value={formData.branch_id}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      branch_id: e.target.value,
                    }))
                  }
                  placeholder="Optional branch identifier"
                />
              </Grid>

              {/* Notes */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={4}
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  placeholder="Additional notes or requirements for suppliers..."
                />
              </Grid>

              {/* Information Alert */}
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    <strong>Note:</strong> After creating the RFQ, you'll be
                    able to send it to the selected suppliers. They can then
                    submit their quotations for your review and comparison.
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <LoadingButton
            onClick={handleSubmit}
            loading={saving}
            variant="contained"
            disabled={loading}
          >
            {rfq ? "Update RFQ" : "Create RFQ"}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};
